/**
 * Zeichenhilfen (PV-D04, PV-D06).
 *
 * Daecher sind fast immer rechtwinklig. Ohne Fang wird das Nachzeichnen muehsam
 * und ungenau — deshalb rastet die Richtung einer neuen Kante auf ein Vielfaches
 * von 90 Grad zur vorigen ein, sobald sie nahe genug dran liegt.
 *
 * Gefangen wird die Richtung, nicht das Mass: Die Laenge, die der Nutzer
 * aufgezogen hat, bleibt erhalten.
 */
import { bezugspunkt, nachGeo, nachGrundriss } from './koordinaten.js';
import { flaecheninhalt } from './polygon.js';
import {} from './typen.js';
/** Ab welcher Abweichung nicht mehr gefangen wird. */
export const FANGWINKEL_GRAD = 12;
/** Kuerzeste Strecke, die noch als eigene Kante zaehlt. */
const MINDESTLAENGE_M = 0.05;
const winkel = (von, nach) => Math.atan2(nach.y - von.y, nach.x - von.x);
/**
 * Einen Punkt an ein bestehendes Polygon anfuegen, mit Fang.
 *
 * Faellt der neue Punkt praktisch auf den vorigen, wird er verworfen — sonst
 * entstehen Kanten der Laenge null, und daraus wird spaeter ein Polygon, das
 * keine Flaeche hat.
 */
export function fuegeMitFangAn(bisher, neu, fangwinkelGrad = FANGWINKEL_GRAD) {
    if (bisher.length === 0)
        return [neu];
    const bezug = bezugspunkt([...bisher, neu]);
    const metrisch = bisher.map((p) => nachGrundriss(p, bezug));
    const letzter = metrisch[metrisch.length - 1];
    const ziel = nachGrundriss(neu, bezug);
    const laenge = Math.hypot(ziel.x - letzter.x, ziel.y - letzter.y);
    if (laenge < MINDESTLAENGE_M)
        return bisher;
    if (bisher.length < 2)
        return [...bisher, neu];
    const vorletzter = metrisch[metrisch.length - 2];
    const vorige = winkel(vorletzter, letzter);
    const gewuenscht = winkel(letzter, ziel);
    // Vielfache von 90 Grad zur vorigen Kante — deckt rechte Winkel und
    // Parallelen in einem ab. Das Zurueckgehen (180 Grad) ist ausgenommen,
    // sonst faellt der neue Punkt auf den vorletzten zurueck.
    let bester;
    let kleinsteAbweichung = fangwinkelGrad;
    for (const schritt of [-90, 0, 90]) {
        const kandidat = vorige + (schritt * Math.PI) / 180;
        // Ueber atan2 gewickelt: `%` bleibt bei negativem Operanden negativ, und
        // dann kam fuer eine Kante knapp neben dem Kandidaten rund 360 Grad heraus.
        const d = gewuenscht - kandidat;
        const abweichung = Math.abs(Math.atan2(Math.sin(d), Math.cos(d)));
        const abweichungGrad = (abweichung * 180) / Math.PI;
        if (abweichungGrad < kleinsteAbweichung) {
            kleinsteAbweichung = abweichungGrad;
            bester = kandidat;
        }
    }
    if (bester === undefined)
        return [...bisher, neu];
    const gefangen = {
        x: letzter.x + Math.cos(bester) * laenge,
        y: letzter.y + Math.sin(bester) * laenge,
    };
    return [...bisher, nachGeo(gefangen, bezug)];
}
/**
 * Welche Kante des Polygons liegt dem Klick am naechsten?
 * Damit wird die Traufkante ausgewaehlt (PV-D10).
 */
export function naechsteKante(polygon, klick) {
    const bezug = bezugspunkt(polygon);
    const metrisch = polygon.map((p) => nachGrundriss(p, bezug));
    const ziel = nachGrundriss(klick, bezug);
    let besterIndex = 0;
    let kleinster = Infinity;
    for (let i = 0; i < metrisch.length; i++) {
        const abstand = abstandZuStrecke(ziel, metrisch[i], metrisch[(i + 1) % metrisch.length]);
        if (abstand < kleinster) {
            kleinster = abstand;
            besterIndex = i;
        }
    }
    return besterIndex;
}
export function abstandZuStrecke(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const laenge = dx * dx + dy * dy;
    if (laenge < 1e-12)
        return Math.hypot(p.x - a.x, p.y - a.y);
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / laenge));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
/** Streckenmass auf dem Luftbild (PV-D04). */
export function streckeM(a, b) {
    const bezug = bezugspunkt([a, b]);
    const pa = nachGrundriss(a, bezug);
    const pb = nachGrundriss(b, bezug);
    return Math.hypot(pb.x - pa.x, pb.y - pa.y);
}
/**
 * Taugt der gezeichnete Ring als Flaeche?
 *
 * Drei Punkte allein genuegen nicht: Fallen zwei zusammen oder liegen alle auf
 * einer Linie, entsteht eine Flaeche ohne Inhalt — und die faellt erst auf,
 * wenn das Modulraster leer bleibt.
 */
export function ringIstBrauchbar(ring, mindestflaecheM2 = 1) {
    if (ring.length < 3)
        return false;
    const bezug = bezugspunkt(ring);
    const metrisch = ring.map((p) => nachGrundriss(p, bezug));
    for (let i = 0; i < metrisch.length; i++) {
        for (let j = i + 1; j < metrisch.length; j++) {
            const abstand = Math.hypot(metrisch[i].x - metrisch[j].x, metrisch[i].y - metrisch[j].y);
            if (abstand < MINDESTLAENGE_M)
                return false;
        }
    }
    return flaecheninhalt(metrisch) >= mindestflaecheM2;
}
/**
 * Die beiden Schenkel einer Pfeilspitze an `spitze`, Pfeil kommt von `von`.
 * Rückgabe als Linienzug: Schenkel – Spitze – Schenkel.
 *
 * Die Länge kommt in Metern, die Karte rechnet sie aus der Zoomstufe — so
 * bleibt die Spitze am Schirm gleich groß, egal wie weit man hineinzoomt.
 */
export function pfeilspitze(spitze, von, laengeM, oeffnungGrad = 25) {
    const bezug = bezugspunkt([spitze, von]);
    const s = nachGrundriss(spitze, bezug);
    const v = nachGrundriss(von, bezug);
    const laenge = Math.hypot(v.x - s.x, v.y - s.y);
    if (laenge < 1e-9)
        return [spitze];
    const ux = (v.x - s.x) / laenge;
    const uy = (v.y - s.y) / laenge;
    const w = (oeffnungGrad * Math.PI) / 180;
    const schenkel = (vorzeichen) => {
        const c = Math.cos(vorzeichen * w);
        const n = Math.sin(vorzeichen * w);
        return nachGeo({ x: s.x + laengeM * (ux * c - uy * n), y: s.y + laengeM * (ux * n + uy * c) }, bezug);
    };
    return [schenkel(1), spitze, schenkel(-1)];
}
/**
 * Die Endstriche einer Maßlinie: quer zur Linie, mittig auf den Endpunkten.
 * Wie auf einem Plan — sonst sieht eine Bemaßung aus wie eine Kante.
 */
export function massendstriche(a, b, laengeM) {
    const bezug = bezugspunkt([a, b]);
    const pa = nachGrundriss(a, bezug);
    const pb = nachGrundriss(b, bezug);
    const laenge = Math.hypot(pb.x - pa.x, pb.y - pa.y);
    if (laenge < 1e-9)
        return [[a], [b]];
    const nx = (-(pb.y - pa.y) / laenge) * (laengeM / 2);
    const ny = ((pb.x - pa.x) / laenge) * (laengeM / 2);
    const strich = (p) => [
        nachGeo({ x: p.x - nx, y: p.y - ny }, bezug),
        nachGeo({ x: p.x + nx, y: p.y + ny }, bezug),
    ];
    return [strich(pa), strich(pb)];
}
/**
 * Welche Kante des geänderten Rings entspricht der Kante `index` des alten?
 *
 * Traufkante und Referenzmaß hängen an einem Kantenindex. Wer beim Bearbeiten
 * eine Ecke einfügt oder entfernt, verschiebt alle Indizes dahinter — vorher
 * zeigte die Traufe danach auf eine andere Kante, der Azimut sprang, und ein
 * Referenzmaß hinter dem Ende des Rings riss die Planung ganz. Gewählt wird
 * die Kante, deren Enden am nächsten an den alten liegen.
 */
export function ordneKanteZu(alt, neu, index) {
    if (neu.length === 0)
        return 0;
    if (alt.length === 0)
        return Math.min(Math.max(0, index), neu.length - 1);
    const bezug = bezugspunkt(alt);
    const a = alt.map((p) => nachGrundriss(p, bezug));
    const n = neu.map((p) => nachGrundriss(p, bezug));
    const i = ((index % a.length) + a.length) % a.length;
    const p1 = a[i];
    const p2 = a[(i + 1) % a.length];
    const richtung = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    let beste = 0;
    let besterAbstand = Infinity;
    for (let j = 0; j < n.length; j++) {
        const q1 = n[j];
        const q2 = n[(j + 1) % n.length];
        // Gleiche Laufrichtung ist Pflicht — sonst gewinnt bei einer schmalen
        // Fläche die gegenüberliegende Kante.
        const d = Math.atan2(q2.y - q1.y, q2.x - q1.x) - richtung;
        if (Math.abs(Math.atan2(Math.sin(d), Math.cos(d))) > Math.PI / 4)
            continue;
        const abstand = Math.hypot(q1.x - p1.x, q1.y - p1.y) + Math.hypot(q2.x - p2.x, q2.y - p2.y);
        if (abstand < besterAbstand) {
            besterAbstand = abstand;
            beste = j;
        }
    }
    return beste;
}
//# sourceMappingURL=zeichnen.js.map